import { startOfCalendarWeek } from '~/calculations/calendar'
import {
  ANCHOR_COPY,
  anchorEligibility,
  notTodayUntil,
  ROUTINE_ANCHOR_KINDS,
  ROUTINE_ANCHOR_LABELS,
  type RoutineAnchorInput,
  type RoutineAnchorKind,
  type RoutineAnchorRecord,
  snoozeUntil,
} from '~/models/routineAnchor'
import { REMINDER_AFTER_DAYS } from '~/services/backupReminderService'
import { isWeekFinalized } from '~/services/dailyCheckService'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { listMeasurementSessions } from '~/storage/repositories/measurementRepository'
import { getLastBackupAt } from '~/storage/repositories/metaRepository'
import { listPhotos } from '~/storage/repositories/photoRepository'
import {
  getRoutineAnchor,
  listEnabledRoutineAnchors,
  putRoutineAnchor,
  setAnchorSuppression,
} from '~/storage/repositories/routineAnchorRepository'
import { listStrengthEntries } from '~/storage/repositories/strengthEntryRepository'
import { listWeeklyReviewsForWeek } from '~/storage/repositories/weeklyReviewRepository'
import { getWeightEntry } from '~/storage/repositories/weightRepository'
import type { CalendarDate, TimeOfDay } from '~/types/dates'
import { calendarDateFrom, unwrapDate, unwrapTime } from '~/types/dates'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'
import { getEffectiveWeekStartDay } from './weekBoundaryService'

/**
 * Deciding what to put in front of the user, and when (D-099, as amended at C6.5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THERE IS NO `completeAnchor`, AND THAT IS THE DESIGN
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  D-099's table says Complete writes nothing: "the underlying task state makes
 *  the reminder obsolete." **A weigh-in anchor stops prompting because
 *  `weightEntries` holds today's date**, not because anything was written to the
 *  anchor. A completion write would be a second, competing representation of
 *  task state — and the moment two representations disagree, the app has to pick
 *  one, which is the failure D-099 §D refuses for the checklist one level up.
 *
 *  The cost is that this file reads six stores. That is the honest price of not
 *  keeping a duplicate.
 *
 *  **Eligibility is also gated by cadence.** An anchor is otherwise open from its
 *  start time to the end of *every* day, so a measurement anchor at 08:00 would
 *  prompt daily for a weekly task. No cadence is invented here: weigh-in is
 *  daily, the weekly tasks use the user's own `weekStartDay`, and backup reuses
 *  `REMINDER_AFTER_DAYS` from the backup reminder rather than restating it.
 */

export interface AnchorPrompt {
  readonly kind: RoutineAnchorKind
  readonly label: string
  readonly openedAt: TimeOfDay
  /** Copy that states what will happen, and never implies an interruption. */
  readonly note: string
}

/**
 * At most two prompts at once.
 *
 * Today's job is to make the next useful thing obvious. A list of six equally
 * weighted prompts hands the prioritising back to the user, which is the one
 * job the app exists to do — the same reasoning that caps the combiner's
 * supporting actions at two.
 */
export const MAX_ANCHOR_PROMPTS = 2

export async function saveAnchor(
  db: SolaDatabase,
  input: RoutineAnchorInput,
  now: Date,
): Promise<Result<RoutineAnchorRecord, StorageError>> {
  return putRoutineAnchor(db, input, now)
}

/** Snooze: later today, clamped so it cannot silently become "not today". */
export async function snoozeAnchor(
  db: SolaDatabase,
  kind: RoutineAnchorKind,
  now: Date,
): Promise<Result<RoutineAnchorRecord | undefined, StorageError>> {
  return setAnchorSuppression(db, kind, snoozeUntil(now), now)
}

/** Not today: until tomorrow's window opens. */
export async function deferAnchorToTomorrow(
  db: SolaDatabase,
  kind: RoutineAnchorKind,
  now: Date,
): Promise<Result<RoutineAnchorRecord | undefined, StorageError>> {
  const anchor = await getRoutineAnchor(db, kind)
  if (!anchor.ok) return anchor
  if (anchor.value === undefined) return ok(undefined)

  return setAnchorSuppression(db, kind, notTodayUntil(anchor.value, now), now)
}

/**
 * Whether this anchor's task still wants doing in its current period.
 *
 * Reads the task's own records. **Nothing here is written by the anchor**, which
 * is what makes a prompt disappear the moment the user does the thing — from
 * anywhere in the app, not only from the prompt.
 */
export async function isAnchorTaskOutstanding(
  db: SolaDatabase,
  kind: RoutineAnchorKind,
  now: Date,
): Promise<Result<boolean, StorageError>> {
  const today = calendarDateFrom(now)

  if (kind === 'weighIn') {
    const entry = await getWeightEntry(db, today)
    if (!entry.ok) return entry
    return ok(entry.value === undefined)
  }

  if (kind === 'backup') {
    const lastBackupAt = await getLastBackupAt(db)
    if (!lastBackupAt.ok) return lastBackupAt
    if (lastBackupAt.value === undefined) return ok(true)

    const due = new Date(lastBackupAt.value)
    due.setDate(due.getDate() + REMINDER_AFTER_DAYS)
    return ok(now >= due)
  }

  const weekStart = await currentWeekStart(db, now)
  if (!weekStart.ok) return weekStart

  return doneThisWeek(db, kind, weekStart.value, today)
}

async function currentWeekStart(
  db: SolaDatabase,
  now: Date,
): Promise<Result<CalendarDate, StorageError>> {
  const weekStartDay = await getEffectiveWeekStartDay(db, now)
  if (!weekStartDay.ok) return weekStartDay
  return ok(startOfCalendarWeek(calendarDateFrom(now), weekStartDay.value))
}

/** `true` when the weekly task has not been done in the week containing today. */
async function doneThisWeek(
  db: SolaDatabase,
  kind: Exclude<RoutineAnchorKind, 'weighIn' | 'backup'>,
  weekStart: CalendarDate,
  today: CalendarDate,
): Promise<Result<boolean, StorageError>> {
  const from = unwrapDate(weekStart)
  const to = unwrapDate(today)
  const inWeek = (date: string): boolean => date >= from && date <= to

  if (kind === 'measurement') {
    const sessions = await listMeasurementSessions(db)
    if (!sessions.ok) return sessions
    return ok(!sessions.value.some((session) => inWeek(unwrapDate(session.date))))
  }

  if (kind === 'photos') {
    const photos = await listPhotos(db)
    if (!photos.ok) return photos
    return ok(!photos.value.some((photo) => inWeek(unwrapDate(photo.capturedDate))))
  }

  if (kind === 'strengthUpdate') {
    const sets = await listStrengthEntries(db)
    if (!sets.ok) return sets
    return ok(!sets.value.some((entry) => inWeek(unwrapDate(entry.date))))
  }

  /* The weekly review, which is the one task whose "done" is a status rather
     than the existence of a record — a draft is a week in progress, not a week
     answered. `isWeekFinalized` is the single authority on that distinction and
     is reused rather than restated; the correction boundary already depends on
     it, and two comparisons against the same status string is how they drift. */
  const reviews = await listWeeklyReviewsForWeek(db, weekStart)
  if (!reviews.ok) return reviews
  return ok(!isWeekFinalized(reviews.value))
}

/**
 * The anchors to show on Today right now.
 *
 * Returns an empty list rather than a placeholder when nothing is open, and the
 * caller decides whether to render the region at all — the same shape as
 * `getDailyNutritionPrompt` returning `undefined` rather than an empty task.
 */
export async function getAnchorPrompts(
  db: SolaDatabase,
  now: Date,
): Promise<Result<readonly AnchorPrompt[], StorageError>> {
  const anchors = await listEnabledRoutineAnchors(db)
  if (!anchors.ok) return anchors

  const open = anchors.value.filter((anchor) => anchorEligibility(anchor, now).kind === 'open')

  const prompts: AnchorPrompt[] = []
  /* Iterated in vocabulary order rather than in storage order, so which two
     prompts appear is stable rather than dependent on the order the user
     happened to configure them. */
  for (const kind of ROUTINE_ANCHOR_KINDS) {
    if (prompts.length >= MAX_ANCHOR_PROMPTS) break

    const anchor = open.find((candidate) => candidate.kind === kind)
    if (anchor?.startTime === undefined) continue

    const outstanding = await isAnchorTaskOutstanding(db, kind, now)
    if (!outstanding.ok) return outstanding
    if (!outstanding.value) continue

    prompts.push({
      kind,
      label: ROUTINE_ANCHOR_LABELS[kind],
      openedAt: anchor.startTime,
      note: ANCHOR_COPY.windowOpened(ROUTINE_ANCHOR_LABELS[kind], unwrapTime(anchor.startTime)),
    })
  }

  return ok(prompts)
}

/** Whether the user has configured any anchor at all, which decides the region. */
export async function hasEnabledAnchors(db: SolaDatabase): Promise<Result<boolean, StorageError>> {
  const anchors = await listEnabledRoutineAnchors(db)
  if (!anchors.ok) return anchors
  return ok(anchors.value.some((anchor) => anchor.startTime !== undefined))
}
